import {
  CreateScheduledEventRequestDto,
  ScheduledEventDto,
  ScheduledEventFormSchema,
  ScheduledEventFormType,
  UpdateScheduledEventRequestDto,
  UserDto,
} from '@act/shared/data-transfer-objects';
import { FormBuilder } from '@act/storybook';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { TrackingProvider } from 'react-components/tracking';
import { autoSaveNotesRequests } from 'redux/requests/auto-save.requests';
import { scheduledEventRequests } from 'redux/requests/scheduled-event.requests';
import {
  createScheduledEventSuccess,
  updateScheduledEventSuccess,
} from 'redux/slices/scheduledEventSlice';
import { useAppDispatch } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import { scheduledEventDateTimeHelpers } from 'utilities/date-time-generator';
import { useToast } from '../app/ReusableToast';
import CompositeDialog from '../dialog/compositeDialog';
import { RichTextEditorRef } from './rich-text-editor';
import { EventTypeExtended } from './scheduled-event-dialog-form-types';

const ANYTIME = -1;
export const DATE_FORMAT = 'yyyy-MM-dd';

const defaultDate = DateTime.now().toFormat(DATE_FORMAT);

export interface ScheduledEventDialogInterfaceProps {
  user: UserDto;
  scheduledEvent?: ScheduledEventDto;
}

interface ScheduledEventDialogProps extends ScheduledEventDialogInterfaceProps {
  handleClose: () => void;
  mode: 'Create' | 'Edit' | 'Next';
  open: boolean;
}

const ScheduledEventDialog = ({
  handleClose,
  mode,
  user,
  scheduledEvent,
  open,
}: ScheduledEventDialogProps) => {
  const { control, handleSubmit, setValue, watch, formState } =
    useForm<ScheduledEventFormType>({
      defaultValues: {
        eventType: scheduledEvent?.type || EventTypeExtended.CALL,
        date: scheduledEvent?.date ? scheduledEvent.date : defaultDate,
        time:
          scheduledEventDateTimeHelpers.getTimeValueFromEpochMillis(
            scheduledEvent?.time
          ) || ANYTIME,
        note: scheduledEvent?.note || '',
        visitLocationId:
          scheduledEvent?.visitLocation &&
          typeof scheduledEvent.visitLocation === 'object' &&
          'id' in scheduledEvent.visitLocation
            ? scheduledEvent.visitLocation.id
            : scheduledEvent && typeof scheduledEvent.visitLocation === 'string'
            ? -1
            : undefined,
        otherLocation:
          scheduledEvent && typeof scheduledEvent.visitLocation === 'string'
            ? scheduledEvent.visitLocation
            : null,
      },
      resolver: zodResolver(ScheduledEventFormSchema),
    });

  const addresses = user?.addresses;
  const visitLocationId = watch('visitLocationId');
  const eventType = watch('eventType');

  const dispatch = useAppDispatch();
  const toast = useToast();

  // Ref to access RichTextEditor methods
  const richTextEditorRef = useRef<RichTextEditorRef>(null);

  const [_deletedAutoSave, _isAutoSaveDeleting, deleteAutoSave] = useApiCall(
    autoSaveNotesRequests.deleteAutoSavedNote
  );

  const [autoSavedNote, _isLoadingGet, getAutoSavedNote] = useApiCall(
    autoSaveNotesRequests.getAutoSavedNote
  );

  const [_autoSaveAction, _isLoadingSave, postAutoSavedNote] = useApiCall(
    autoSaveNotesRequests.postAutoSavedNote
  );

  const [
    createdScheduledEvent,
    isCreatingScheduledEvent,
    createScheduledEvent,
  ] = useApiCall(scheduledEventRequests.createScheduledEventForUser);
  const [
    updatedScheduledEvent,
    isUpdatingScheduledEvent,
    updateScheduledEvent,
  ] = useApiCall(scheduledEventRequests.updateScheduledEvent);

  // Track whether the update was triggered by a manual save
  const [isManualSave, setIsManualSave] = useState(false);

  useEffect(() => {
    if (visitLocationId !== -1) setValue('otherLocation', null);
  }, [visitLocationId]);

  const addressOptions = useMemo(() => {
    let returnValue = [];
    if (addresses?.length) {
      returnValue = addresses.map(address => {
        return {
          key: address?.id,
          label: `${address?.line1}, ${address?.line2 ?? ''}, ${
            address?.city
          }, ${address?.state} ${address?.zip}`,
          value: address?.id,
        };
      });
    }
    returnValue.push({ key: -1, label: 'Other', value: -1 });
    return returnValue;
  }, [addresses]);

  // Reset location fields when event type changes away from 'visit'
  useEffect(() => {
    if (eventType !== 'visit') {
      setValue('visitLocationId', undefined);
      setValue('otherLocation', null);
    } else if (eventType === 'visit' && !visitLocationId) {
      // When switching to visit, set default to first address if available
      const firstAddressId = addressOptions[0]?.value;
      if (firstAddressId !== undefined && firstAddressId !== -1) {
        setValue('visitLocationId', firstAddressId);
      }
    }
  }, [eventType, visitLocationId, addressOptions, setValue]);

  const timeOptions = useMemo(
    () => [
      { key: ANYTIME, label: 'Anytime', value: ANYTIME },
      ...scheduledEventDateTimeHelpers
        .getTimeOptions(
          scheduledEventDateTimeHelpers.getTimeValueFromEpochMillis(
            scheduledEvent?.time
          )
        )
        .map(option => ({
          key: option.value,
          label: option.label,
          value: option.value,
        })),
    ],
    [scheduledEvent]
  );

  useEffect(() => {
    if (mode === 'Edit') return;

    async function getAutoSaved() {
      try {
        await getAutoSavedNote({
          params: { subjectId: user?.id, type: 'events' },
        });
      } catch (err) {
        console.error(`Error getting autosaved note: ${err}`);
      }
    }

    void getAutoSaved();
  }, []);

  useEffect(() => {
    if (!autoSavedNote) return;
    setValue('note', autoSavedNote.text);
  }, [autoSavedNote]);

  async function handleSave(data: ScheduledEventFormType) {
    // Cancel any pending autosave before proceeding with manual save
    if (richTextEditorRef.current) {
      richTextEditorRef.current.cancelPendingAutoSave();
    }

    // If we cancelled an autosave, the form data might not be up to date
    // Get the current value from the RichTextEditor to ensure we have the latest
    let currentNoteValue: string | undefined;
    if (richTextEditorRef.current) {
      currentNoteValue = richTextEditorRef.current.getCurrentValue();
      data.note = currentNoteValue;
    }

    const dateValue = data.date as string;

    const body:
      | CreateScheduledEventRequestDto
      | UpdateScheduledEventRequestDto = {
      date: dateValue,
      type: data.eventType,
      visitLocationAddressId:
        data.visitLocationId !== -1 ? data.visitLocationId : null,
      visitLocation: data.visitLocationId === -1 ? data.otherLocation : null,
      ...(!!data.note && { note: data.note }),
    };

    // Time is required by Zod validation
    if (data.time === ANYTIME) {
      body.time = null;
    } else {
      const date = DateTime.fromFormat(dateValue, DATE_FORMAT);
      const time = date.plus({ milliseconds: data.time }).toMillis();
      body.time = time;
    }

    try {
      if (scheduledEvent) {
        setIsManualSave(true); // Mark this as a manual save
        updateScheduledEvent({
          params: { userId: user.id, scheduledEventId: scheduledEvent.id },
          body,
        });
      } else {
        createScheduledEvent({
          params: { userId: user.id },
          body,
        });
      }

      // Mark the current value as saved to prevent unnecessary autosaves
      if (richTextEditorRef.current && currentNoteValue) {
        richTextEditorRef.current.markAsSaved(currentNoteValue);
      }
    } catch (err) {
      console.error(`Error saving scheduled event: ${err}`);
      setIsManualSave(false); // Reset flag on error
    }
  }

  useEffect(() => {
    if (createdScheduledEvent || updatedScheduledEvent) {
      if (createdScheduledEvent) {
        dispatch(createScheduledEventSuccess(createdScheduledEvent));
        deleteAutoSave({
          params: { subjectId: user?.id, type: 'events' },
        });
        toast('Scheduled event has been created successfully');
        handleClose();
      }
      if (updatedScheduledEvent && !isUpdatingScheduledEvent && isManualSave) {
        dispatch(updateScheduledEventSuccess(updatedScheduledEvent));
        setIsManualSave(false);
        toast('Scheduled event has been updated successfully');
        handleClose();
      } else if (updatedScheduledEvent && !isUpdatingScheduledEvent) {
        // Auto-save completed, just reset the flag
        setIsManualSave(false);
      }
    }
  }, [createdScheduledEvent, updatedScheduledEvent]);

  async function handleAutoSave(autoSaveNote: string) {
    if (mode === 'Create') {
      postAutoSavedNote({
        params: { subjectId: user?.id, type: 'events' },
        body: { text: autoSaveNote },
      });
    } else {
      const body: CreateScheduledEventRequestDto = {
        note: autoSaveNote,
        type: scheduledEvent?.type,
      };
      const params = { userId: user?.id, scheduledEventId: scheduledEvent?.id };

      // Auto-save doesn't set the manual save flag, so no confirmation will be shown
      updateScheduledEvent({
        params,
        body,
      });
    }
  }

  const formConfig: FormConfig<ScheduledEventFormType> = [
    {
      type: 'label',
      label: 'Select an event type.',
    },
    {
      type: 'input',
      name: 'eventType',
      label: 'Event Type',
      input: 'radioGroup',
      props: {
        options: [
          {
            label: 'Phone call',
            value: 'call',
          },
          {
            label: 'Text message',
            value: 'message',
          },
          {
            label: 'Home visit',
            value: 'visit',
          },
        ],
        row: true,
        trackingName: 'event-type-radio-group',
      },
    },
  ];

  // Add visit location fields only if event type is 'visit'
  if (eventType === 'visit') {
    formConfig.push({
      type: 'input',
      name: 'visitLocationId',
      label: 'Location',
      input: 'select',
      props: {
        options: addressOptions,
        fullWidth: true,
        trackingName: 'visit-location-select',
      },
    });

    // Add other location field only if "Other" is selected
    if (visitLocationId === -1) {
      formConfig.push({
        type: 'input',
        name: 'otherLocation',
        label: 'Location',
        input: 'text',
        props: {
          trackingName: 'other-location-input',
        },
      });
    }
  }

  // Add date and time fields
  formConfig.push(
    {
      type: 'label',
      label: 'Select a time that works best.',
    },
    {
      type: 'group',
      items: [
        {
          type: 'input',
          name: 'date',
          label: 'Date',
          input: 'date',
          props: {
            minDate: DateTime.now(),
            required: true,
            inputType: 'date',
            trackingName: 'event-date-input',
          },
        },
        {
          type: 'input',
          name: 'time',
          label: 'Start time',
          input: 'select',
          props: {
            options: timeOptions,
            fullWidth: true,
            trackingName: 'event-time-select',
          },
        },
      ],
    }
  );
  formConfig.push(
    {
      type: 'label',
      label: 'Leave a note to help you remember what to talk about.',
    },
    {
      type: 'input',
      name: 'note',
      label: 'Note',
      input: 'richText',
      props: {
        autoSaveFn: handleAutoSave,
        size: 'small',
        ref: richTextEditorRef,
        trackingName: 'event-note-input',
      },
    }
  );
  const isSaving = isCreatingScheduledEvent || isUpdatingScheduledEvent;
  return (
    <TrackingProvider contextName="scheduled-event-dialog">
      <CompositeDialog
        handleClose={handleClose}
        maxWidth="md"
        open={open}
        title={`${
          mode === 'Next' ? 'Schedule next event' : 'Schedule event'
        } for ${user.firstName} ${user.lastName}`}
        handleFormSubmit={handleSubmit(data => {
          void handleSave(data);
        })}
        actions={[
          {
            text: 'Cancel',
            callback: () => handleClose(),
            variant: 'outlined',
            disabled: isCreatingScheduledEvent || isUpdatingScheduledEvent,
          },
          {
            text: isSaving
              ? 'Saving...'
              : mode === 'Edit'
              ? 'Save'
              : 'Schedule event',
            type: 'submit',
            variant: 'contained',
            isPrimary: true,
            disabled: isSaving,
          },
        ]}
      >
        {mode === 'Next' && (
          <Box sx={{ padding: '16px 0' }}>
            <Typography variant="bodyLarge">
              Please schedule your next interaction with this member.
            </Typography>
          </Box>
        )}
        <Box sx={{ padding: '16px 0' }}>
          <FormBuilder
            config={formConfig}
            control={control}
            formState={formState}
          />
        </Box>
      </CompositeDialog>
    </TrackingProvider>
  );
};

export default ScheduledEventDialog;
